import { useState, useCallback, MouseEvent } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { Image } from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

import type { PipeItem } from './integrations-search-types';

// ============================================================================
// Colors
// ============================================================================

const TEXT_PRIMARY = wacToken('color.extended.blue1000');
const TEXT_SECONDARY = wacToken('color.neutral.light800');
const TEXT_TAG_SELECTED = wacToken('color.extended.blue1000');

// ============================================================================
// Styles
// ============================================================================

const cardStyle = css({
	display: 'flex',
	flexDirection: 'column',
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '8px',
	padding: token('space.300'),
	cursor: 'pointer',
	boxShadow: token('elevation.shadow.raised'),
	transition: 'box-shadow 0.2s ease, transform 0.2s ease',
	height: '100%',
	boxSizing: 'border-box',
	border: 'none',
	background: 'none',
	textAlign: 'left',
	font: 'inherit',
	color: 'inherit',

	':hover': {
		boxShadow: token('elevation.shadow.overlay'),
		transform: 'translateY(-2px)',
	},

	':focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: 2,
	},
});

const logoAreaStyle = css({
	marginBlockEnd: token('space.200'),
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: 80,
});

const logoStyle = css({
	maxWidth: 80,
	maxHeight: 80,
	objectFit: 'contain',
});

const titleStyle = css({
	margin: `0 0 ${token('space.100')}`,
	fontFamily: 'Charlie Text',
	fontSize: 16,
	fontWeight: 600,
	lineHeight: 1.4,
	color: TEXT_PRIMARY,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	display: '-webkit-box',
	WebkitLineClamp: 2,
	WebkitBoxOrient: 'vertical',
	wordBreak: 'break-word',
});

const descriptionStyle = css({
	margin: `0 0 ${token('space.200')}`,
	fontFamily: 'Charlie Text',
	fontSize: 14,
	lineHeight: 1.5,
	color: TEXT_PRIMARY,
	flex: 1,
	display: '-webkit-box',
	WebkitLineClamp: 3,
	WebkitBoxOrient: 'vertical',
	overflow: 'hidden',
});

const tagsContainerStyle = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: 6,
	marginBlockStart: 'auto',
});

const tagButtonBaseStyle = css({
	background: wacToken('color.neutral.light200'),
	border: 'none',
	borderRadius: 4,
	padding: '4px 8px',
	fontFamily: 'Charlie Text',
	fontSize: 12,
	color: TEXT_SECONDARY,
	cursor: 'pointer',
	transition: 'background-color 0.15s ease, color 0.15s ease',

	':hover': {
		backgroundColor: wacToken('color.neutral.light400'),
	},
});

const tagButtonSelectedStyle = css({
	backgroundColor: wacToken('color.neutral.light500'),
	color: TEXT_TAG_SELECTED,
	fontWeight: 600,
});

const expandButtonStyle = css({
	background: wacToken('color.neutral.light200'),
	border: 'none',
	borderRadius: 4,
	padding: '4px 8px',
	fontFamily: 'Charlie Text',
	fontSize: 12,
	color: TEXT_SECONDARY,
	cursor: 'pointer',
	fontWeight: 600,
	transition: 'background-color 0.15s ease',

	':hover': {
		backgroundColor: wacToken('color.neutral.light400'),
	},
});

// ============================================================================
// Constants
// ============================================================================

const MAX_VISIBLE_TAGS = 3;

// ============================================================================
// Component
// ============================================================================

export interface IntegrationsSearchCardProps {
	item: PipeItem;
	onClick?: (item: PipeItem) => void;
}

export const IntegrationsSearchCard = ({ item, onClick }: IntegrationsSearchCardProps) => {
	const [selectedTag, setSelectedTag] = useState<string | null>(null);
	const [expanded, setExpanded] = useState(false);

	const visibleTags = item.tags.slice(0, MAX_VISIBLE_TAGS);
	const hiddenTags = item.tags.slice(MAX_VISIBLE_TAGS);
	const hasHiddenTags = hiddenTags.length > 0;

	const tagsToRender = expanded ? item.tags : visibleTags;

	const handleTagClick = useCallback((e: MouseEvent<HTMLButtonElement>, tag: string) => {
		e.stopPropagation();
		setSelectedTag((prev) => (prev === tag ? null : tag));
	}, []);

	const handleExpandClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		setExpanded((prev) => !prev);
	}, []);

	return (
		<button type="button" css={cardStyle} onClick={() => onClick?.(item)} aria-label={item.name}>
			<div css={logoAreaStyle}>
				<Image css={logoStyle} src={item.logo} alt={item.name} loading="lazy" />
			</div>

			<h4 css={titleStyle}>{item.name}</h4>

			<p css={descriptionStyle}>{item.description}</p>

			<div css={tagsContainerStyle}>
				{tagsToRender.map((tag) => (
					<button
						key={tag}
						type="button"
						css={[tagButtonBaseStyle, selectedTag === tag && tagButtonSelectedStyle]}
						onClick={(e) => handleTagClick(e, tag)}
					>
						{tag}
					</button>
				))}

				{hasHiddenTags && (
					<button
						type="button"
						css={expandButtonStyle}
						onClick={handleExpandClick}
						aria-label={expanded ? 'Show fewer tags' : 'Show more tags'}
					>
						{expanded ? '×' : '...'}
					</button>
				)}
			</div>
		</button>
	);
};
